import { HStack, useCheckboxGroup } from '@chakra-ui/react';
import React from 'react';

import type { NFTTokenType, TokenType } from 'types/api/token';

import useIsInitialLoading from 'lib/hooks/useIsInitialLoading';
import { Button } from 'toolkit/chakra/button';
import PopoverFilter from 'ui/shared/filters/PopoverFilter';
import TokenTypeCheckboxGroup from 'ui/shared/filters/TokenTypeCheckboxGroup';

export interface TokenTypeFiltersProps<T extends TokenType | NFTTokenType> {
  onRemoveTypeFilter: (nextValue: T) => void;
  selectedValues?: Array<T>;
  onTypeFilterChange: (nextValue: Array<T>) => void;
  defaultAddressFilter?: string;
  isLoading?: boolean;
  endComponent?: React.ReactNode;
}

export default function TokenTypeFilters<T extends TokenType | NFTTokenType>({
  onRemoveTypeFilter,
  onTypeFilterChange,
  selectedValues,
  isLoading,
  endComponent,
}: TokenTypeFiltersProps<T>) {
  const isInitialLoading = useIsInitialLoading(isLoading);
  const { value: typeValue, setValue: setTypeValue } = useCheckboxGroup({ value: selectedValues });

  const handleReset = React.useCallback(() => {
    if (typeValue.length === 0) return;
    setTypeValue([]);
    onTypeFilterChange([]);
  }, [ onTypeFilterChange, setTypeValue, typeValue.length ]);

  return (
    <HStack gap={ 2 }>
      <PopoverFilter
        onRemoveTypeFilter={ onRemoveTypeFilter }
        placeholder="All types"
        selectedValues={ selectedValues }
        filterName="Type"
        contentProps={{ w: '220px' }}
        isLoading={ isInitialLoading }
      >
        <TokenTypeCheckboxGroup<T>
          onChange={ onTypeFilterChange }
          defaultValue={ selectedValues }
          nftOnly={ false }
          value={ typeValue }
          setValue={ setTypeValue }
        />
      </PopoverFilter>

      { endComponent }

      { Boolean(selectedValues?.length) && (
        <Button variant="link" onClick={ handleReset } textStyle="sm" px={ 3 }>
          Reset filters
        </Button>
      ) }
    </HStack>
  );
}
